import { FormApi } from 'final-form';
import { useForm, useFormState } from 'react-final-form';

import { InquiryType } from 'modules/Inquiry/Inquiry.type';
import { AnyConditions } from 'new/form/state/types';

import { ValidationMapType, FieldTypes } from './common/types';
import { shouldShowField } from './hooks/useCanShowField';

export interface ValidateSectionFunction<Type> {
  (form: FormApi, values: FieldTypes<Type>, store: any, conditions: AnyConditions):
    | boolean
    | ((value: any, values: FieldTypes<Type>) => boolean);
}

type ValidateField<Type> = {
  field: string;
  values: FieldTypes<Type>;
  form: FormApi;
  conditions: AnyConditions;
};

type ValidateArrayFields<Type> = {
  arrayValues: Array<any>;
  parentName: string;
  values: FieldTypes<Type>;
  fieldsWithoutParent: string[];
  form: FormApi;
  conditions: AnyConditions;
};

export const validateField = <Type extends InquiryType>({
  field,
  values,
  form,
  conditions,
}: ValidateField<Type>) => {
  return validateFields<Type>({ fields: [field], values, form, conditions });
};

type ValidateFields<Type> = {
  fields: string[];
  values: FieldTypes<Type>;
  form: FormApi;
  conditions: AnyConditions;
};

export const validateArrayFields = <Type extends InquiryType>({
  arrayValues,
  parentName,
  fieldsWithoutParent,
  form,
  values,
  conditions,
}: ValidateArrayFields<Type>) => {
  const fieldNames: Array<string> = [];
  fieldsWithoutParent.forEach((fieldName) => {
    fieldNames.push(...arrayValues.map((_, index) => `${parentName}[${index}].${fieldName}`));
  });

  return validateFields<Type>({ fields: fieldNames, values, form, conditions });
};

export const validateFields = <Type extends InquiryType>({
  fields,
  values,
  form,
  conditions,
}: ValidateFields<Type>) => {
  const visibleFields = fields.filter((name) =>
    shouldShowField<FieldTypes<Type>>(name, values, form, conditions),
  );
  const fieldStates = visibleFields.map((name) => {
    return form.getFieldState(name);
  });
  return fieldStates.every((field) => field?.valid);
};

export const useValidateFields = <Type extends InquiryType>({
  fields,
  conditions,
}: {
  fields: Array<string>;
  conditions: AnyConditions;
}) => {
  const form = useForm();
  const { values } = useFormState<FieldTypes<Type>>();
  return validateFields({ fields, values, form, conditions });
};

export const useValidateArrayFields = <Type extends InquiryType>({
  arrayValues,
  parentName,
  fieldsWithoutParent,
  conditions,
}: Pick<
  ValidateArrayFields<Type>,
  'arrayValues' | 'parentName' | 'fieldsWithoutParent' | 'conditions'
>) => {
  const form = useForm();
  const { values } = useFormState<FieldTypes<Type>>();

  return validateArrayFields<Type>({
    arrayValues,
    parentName,
    fieldsWithoutParent,
    values,
    form,
    conditions,
  });
};

// export const useValidateField = <FieldTypes>({ field }: { field: string }) => {
//   const form = useForm();
//   const { values } = useFormState<FieldTypes>();
//   return validateFields({ fields: [field], values, form, inquiryType: InquiryType.profiMittweida });
// };

type ValidateFieldValidationMap<Type> = {
  validationMap: ValidationMapType<FieldTypes<Type>>;
  conditions: AnyConditions;
};

type ValidateFieldValidationMapArgs<Type> = {
  form: FormApi;
  values: FieldTypes<Type>;
} & ValidateFieldValidationMap<Type>;
/**
 * Don't forget to put your map in src/modules/Inquiry/inquiryFieldValidation/validateField.ts!
 */
export const validateFieldValidationMap = <Type extends InquiryType>({
  validationMap,
  form,
  values,
  conditions,
}: ValidateFieldValidationMapArgs<Type>) => {
  const fieldsToValidate = Object.keys(validationMap).map((key) => key);
  return validateFields<Type>({
    fields: fieldsToValidate,
    values,
    form,
    conditions,
  });
};

export const useValidateFieldValidationMap = <
  Type extends InquiryType = InquiryType.profiMittweida,
>({
  validationMap,
  conditions,
}: ValidateFieldValidationMap<Type>) => {
  const fieldsToValidate = Object.keys(validationMap).map((key) => key);
  return useValidateFields<Type>({
    fields: fieldsToValidate,
    conditions,
  });
};
